/**
 * Notification settings — spec §5-F14, §8.1 `/settings/notifications`.
 *
 * The event × channel matrix, quiet hours, and phone verification — which is a
 * hard gate: §F14 says an unverified number is never sent task content, so the
 * WhatsApp column stays disabled until the code is confirmed.
 */

import { useEffect, useState } from 'react';
import { Check, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import {
  DEFAULT_NOTIFICATION_MATRIX,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_TYPES,
  type NotificationChannel,
  type NotificationType,
} from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/features/auth';
import { useUpdatePreferences, useWhatsappEnabled } from './api';
import { PushDevices } from './PushDevices';
import { PushDiagnostics } from './PushDiagnostics';

type Matrix = Record<string, Record<string, boolean>>;

/**
 * Fills in every event and channel.
 *
 * Two reasons this is not optional: the server's entry schema requires all four
 * channel booleans, so a partial entry is rejected; and a user whose stored
 * matrix predates a new event type should see that event's documented default
 * rather than a blank row.
 */
function completeMatrix(stored: Matrix | undefined): Matrix {
  const complete: Matrix = {};
  for (const type of NOTIFICATION_TYPES) {
    const defaults = (DEFAULT_NOTIFICATION_MATRIX as unknown as Matrix)[type] ?? {};
    const saved = stored?.[type] ?? {};
    complete[type] = Object.fromEntries(
      NOTIFICATION_CHANNELS.map((channel) => [
        channel,
        saved[channel] ?? defaults[channel] ?? false,
      ]),
    );
  }
  return complete;
}

const CHANNEL_LABEL: Record<NotificationChannel, string> = {
  inApp: 'In-app',
  email: 'Email',
  whatsapp: 'WhatsApp',
  push: 'Push',
};

/** Human labels — the raw keys are for the wire, not for people. */
const EVENT_LABEL: Partial<Record<NotificationType, string>> = {
  'task.assigned': 'Assigned to me',
  'task.unassigned': 'Unassigned from me',
  'task.mentioned': 'Mentioned',
  'task.comment': 'Comment on something I watch',
  'task.status_changed': 'Status changed on something I watch',
  'task.due_soon': 'Due within 24 hours',
  'task.overdue': 'Overdue',
  'task.blocked': 'Blocked',
  'task.unblocked': 'Unblocked',
  'task.review_requested': 'Review requested',
  'task.review_approved': 'Review approved',
  'task.review_rejected': 'Review rejected',
  'task.reported_completed': 'Task I reported was completed',
  'project.added': 'Added to a project',
  'sprint.started': 'Sprint started',
  'sprint.closed': 'Sprint closed',
  'automation.fired': 'Automation fired',
  'leave.requested': 'Leave requested (to approve)',
  'leave.approved': 'My leave approved',
  'leave.rejected': 'My leave rejected',
  'digest.founder': 'Founder daily digest',
  'digest.member': 'My daily digest',
  'digest.lead_weekly': 'Weekly project summary',
  'attendance.reminder': 'Attendance reminders',
  'attendance.correction': 'Attendance corrections',
  'admin.alert': 'Admin alerts',
  'export.ready': 'Export ready',
};

function PhoneVerification() {
  const { user, refreshMe } = useAuth();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'idle' | 'sent'>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user?.phoneVerified) {
    return (
      <div className="vt-surface flex items-center gap-2 p-3" data-testid="phone-verified">
        <ShieldCheck className="size-4 text-[var(--accent)]" />
        <span className="text-[13px]">Phone verified</span>
        <Badge tone="accent">WhatsApp available</Badge>
      </div>
    );
  }

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post('/users/me/phone/verify-start', { phone });
      setStage('sent');
      toast.success('Code sent — it expires in 10 minutes');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the code');
    } finally {
      setBusy(false);
    }
  }

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post('/users/me/phone/verify-confirm', { code });
      await refreshMe();
      toast.success('Phone verified — WhatsApp notifications are now available');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That code was not accepted');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vt-surface flex flex-col gap-2 p-3" data-testid="phone-verification">
      <div className="flex items-center gap-2">
        <Smartphone className="size-4 text-[var(--text-muted)]" />
        <span className="text-[13px] font-medium">Verify your phone for WhatsApp</span>
      </div>
      <p className="text-[12px] vt-muted">
        WhatsApp notifications are off until the number is confirmed — an unverified number is never
        sent task content.
      </p>
      {stage === 'idle' ? (
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="phone">Phone number</Label>
            <Input
              id="phone"
              data-testid="phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91…"
            />
          </div>
          <Button
            variant="subtle"
            size="sm"
            data-testid="phone-send-code"
            disabled={!phone || busy}
            onClick={() => void send()}
          >
            {busy ? <Loader2 className="animate-spin" /> : null}
            Send code
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="code">6-digit code</Label>
            <Input
              id="code"
              data-testid="phone-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              maxLength={6}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            data-testid="phone-confirm"
            disabled={code.length !== 6 || busy}
            onClick={() => void confirm()}
          >
            {busy ? <Loader2 className="animate-spin" /> : <Check />}
            Confirm
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="text-[12px] text-[var(--danger)]">
          {error}
        </p>
      )}
    </div>
  );
}

export function NotificationSettingsPage() {
  const { user, refreshMe } = useAuth();
  const update = useUpdatePreferences();
  const whatsappEnabled = useWhatsappEnabled();

  // Ruling 5: the column simply is not there when the channel is off, rather
  // than being present and disabled — a greyed-out control still says "this
  // exists, go and find it".
  const channels = NOTIFICATION_CHANNELS.filter((c) => c !== 'whatsapp' || whatsappEnabled);

  const [matrix, setMatrix] = useState<Matrix>({});
  const [quiet, setQuiet] = useState({ enabled: true, start: '21:00', end: '08:00' });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!user) return;
    setMatrix(completeMatrix(user.preferences.notificationMatrix));
    setQuiet(user.preferences.quietHours ?? { enabled: true, start: '21:00', end: '08:00' });
  }, [user?._id]);

  const phoneVerified = Boolean(user?.phoneVerified);

  function toggle(type: string, channel: string): void {
    setMatrix((current) => ({
      ...current,
      [type]: { ...(current[type] ?? {}), [channel]: !(current[type]?.[channel] ?? false) },
    }));
    setDirty(true);
  }

  async function save(): Promise<void> {
    // The whole matrix is replaced, never dot-patched: these keys contain dots
    // (`task.assigned`), which Mongo would read as a nested path. Every entry
    // carries all four channels, which the server's schema requires.
    await update.mutateAsync({ notificationMatrix: completeMatrix(matrix), quietHours: quiet });
    await refreshMe();
    setDirty(false);
    toast.success('Notification preferences saved');
  }

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle="What reaches you, where, and when."
        actions={
          <Button variant="primary" size="sm" disabled={!dirty || update.isPending} onClick={() => void save()}>
            {update.isPending ? <Loader2 className="animate-spin" /> : <Check />}
            Save
          </Button>
        }
      />

      <div className="flex flex-col gap-4 p-4">
        {/* Verifying a number for a channel that does not exist is busywork. */}
        {whatsappEnabled && <PhoneVerification />}

        {/* V6-01 §4: the Push column above is only meaningful once a browser
            is registered, and this is the only place that asks. */}
        <PushDevices />

        {/* V7-01 §1 — the permanent half of the push fix. The gesture bug is
            fixed once; this is what makes the next failure findable. */}
        <PushDiagnostics />

        <section className="vt-surface p-3">
          <h2 className="text-[13px] font-medium">Quiet hours</h2>
          <p className="mb-2 text-[12px] vt-muted">
            Non-P0 notifications queue until quiet hours end. P0 always goes through.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={quiet.enabled}
                onChange={(e) => {
                  setQuiet((q) => ({ ...q, enabled: e.target.checked }));
                  setDirty(true);
                }}
              />
              Enabled
            </label>
            <div className="flex flex-col gap-1">
              <Label htmlFor="quiet-start">From</Label>
              <Input
                id="quiet-start"
                type="time"
                value={quiet.start}
                onChange={(e) => {
                  setQuiet((q) => ({ ...q, start: e.target.value }));
                  setDirty(true);
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="quiet-end">To</Label>
              <Input
                id="quiet-end"
                type="time"
                value={quiet.end}
                onChange={(e) => {
                  setQuiet((q) => ({ ...q, end: e.target.value }));
                  setDirty(true);
                }}
              />
            </div>
          </div>
        </section>

        <section className="vt-surface overflow-x-auto" data-testid="notification-matrix">
          <table className="w-full min-w-[520px] border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  Event
                </th>
                {channels.map((channel) => (
                  <th key={channel} scope="col" className="px-3 py-2 text-center font-medium">
                    {CHANNEL_LABEL[channel]}
                    {channel === 'whatsapp' && !phoneVerified && (
                      <div className="text-[10px] font-normal vt-muted">verify phone</div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {NOTIFICATION_TYPES.map((type) => (
                <tr key={type} className="border-b border-[var(--border)] last:border-0">
                  <th scope="row" className="px-3 py-1.5 text-left font-normal">
                    {EVENT_LABEL[type] ?? type}
                  </th>
                  {channels.map((channel) => {
                    const locked = channel === 'whatsapp' && !phoneVerified;
                    return (
                      <td key={channel} className="px-3 py-1.5 text-center">
                        <input
                          type="checkbox"
                          aria-label={`${EVENT_LABEL[type] ?? type} via ${CHANNEL_LABEL[channel]}`}
                          disabled={locked}
                          checked={matrix[type]?.[channel] ?? false}
                          onChange={() => toggle(type, channel)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </>
  );
}
